import { Box, Divider, Popover, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { AddCircle, ArrowDown2, Shop, TickCircle } from "iconsax-react";
import { Link } from "@tanstack/react-router";
import { memo, useCallback, useEffect, useMemo, useState, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";

import type { ShopSwitcherProps } from "./ShopSwitcher.types";
import * as styles from "./ShopSwitcher.styles";

export type { ShopSwitcherProps } from "./ShopSwitcher.types";

export const ShopSwitcher = memo(function ShopSwitcher({
  stores,
  activeStoreId,
  onSelectStore,
  storeRoles = {},
  onOpenChange,
}: ShopSwitcherProps) {
  const { t } = useTranslation("common");
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  const activeStore = useMemo(
    () => stores.find((store) => store.id === activeStoreId) ?? stores[0],
    [stores, activeStoreId],
  );

  const activeRole = activeStore ? storeRoles[activeStore.id] : undefined;

  const handleToggle = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl((prev) => (prev ? null : event.currentTarget));
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSelect = useCallback(
    (id: number) => {
      onSelectStore(id);
      handleClose();
    },
    [onSelectStore, handleClose],
  );

  if (!activeStore) return null;

  return (
    <Box sx={styles.triggerWrap}>
      <Box component="button" type="button" sx={styles.trigger} onClick={handleToggle} aria-label={activeStore.name}>
        <Box sx={styles.storeIcon}>
          <Shop size={16} variant="Bold" color={theme.palette.primary.main} />
        </Box>
        <Box sx={styles.storeText}>
          <Typography component="span" sx={styles.storeName}>
            {activeStore.name}
          </Typography>
          <Typography component="span" sx={styles.storeRole}>
            {activeRole ?? t("navbar.storeManager")}
          </Typography>
        </Box>
        <Box sx={styles.storeChevron}>
          <ArrowDown2
            size={14}
            variant="Linear"
            color={theme.palette.text.secondary}
          />
        </Box>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: styles.paper } }}
      >
        <Typography sx={styles.menuLabel}>{t("shopSwitcher.myShops")}</Typography>
        {stores.map((store) => {
          const isActive = store.id === activeStoreId;
          const role = storeRoles[store.id];
          return (
            <Box
              key={store.id}
              sx={styles.shopRow(isActive)}
              onClick={() => handleSelect(store.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleSelect(store.id);
              }}
            >
              <Box sx={styles.storeIcon}>
                <Shop size={17} variant="Bold" color={theme.palette.primary.main} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {store.name}
                </Typography>
                {role ? (
                  <Typography variant="caption" color="text.secondary">
                    {role}
                  </Typography>
                ) : null}
              </Box>
              {isActive ? (
                <TickCircle size={18} variant="Bold" color={theme.palette.primary.main} />
              ) : null}
            </Box>
          );
        })}
        <Divider sx={styles.divider} />
        <Box component={Link} to="/shops/create" sx={styles.createLink} onClick={handleClose}>
          <AddCircle size={18} variant="Bold" color={theme.palette.primary.main} />
          {t("shopSwitcher.createShop")}
        </Box>
      </Popover>
    </Box>
  );
});
